/**
 * useABCompare — iter-247
 *
 * Tiny self-contained A/B player for two AudioBuffers. Used by the
 * TakeHistoryModal so a user can pick any two takes and flip between
 * them on the fly to decide which one to keep.
 *
 * Implementation notes:
 *   - We allocate ONE AudioContext per hook instance (closed on cleanup).
 *   - Only one buffer plays at a time — switching slots stops the current
 *     source and starts the other from offset 0. Simpler than scheduling
 *     two parallel sources in lock-step, and avoids click artifacts when
 *     the two takes have different lengths.
 *   - Returns a `time` value updated via rAF so the modal can show a
 *     progress bar.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export default function useABCompare() {
  const ctxRef = useRef(null);
  const sourceRef = useRef(null);
  const startedAtRef = useRef(0);   // ctx.currentTime when current source started
  const [activeSlot, setActiveSlot] = useState(null); // 'A' | 'B' | null
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const rafRef = useRef(0);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    return ctxRef.current;
  };

  const stop = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(0); } catch (e) { void e; }
      try { sourceRef.current.disconnect(); } catch (e) { void e; }
      sourceRef.current = null;
    }
    cancelAnimationFrame(rafRef.current);
    setActiveSlot(null);
    setTime(0);
  }, []);

  const play = useCallback((slot, buffer) => {
    if (!buffer) return;
    stop();
    const ctx = ensureCtx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.onended = () => {
      // Natural end — only clear if THIS source is still the active one.
      if (sourceRef.current === src) {
        sourceRef.current = null;
        setActiveSlot(null);
        setTime(0);
        cancelAnimationFrame(rafRef.current);
      }
    };
    sourceRef.current = src;
    startedAtRef.current = ctx.currentTime;
    setDuration(buffer.duration);
    setActiveSlot(slot);
    try { src.start(0); } catch (e) { void e; }

    // Drive a small progress tick at ~30 Hz.
    const tick = () => {
      if (!sourceRef.current || sourceRef.current !== src) return;
      const elapsed = ctx.currentTime - startedAtRef.current;
      setTime(Math.min(elapsed, buffer.duration));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stop]);

  const toggle = useCallback((slot, buffer) => {
    if (activeSlot === slot) stop();
    else play(slot, buffer);
  }, [activeSlot, play, stop]);

  const swap = useCallback((slotABuffer, slotBBuffer) => {
    if (activeSlot === "A") play("B", slotBBuffer);
    else if (activeSlot === "B") play("A", slotABuffer);
    else play("A", slotABuffer); // start fresh on A
  }, [activeSlot, play]);

  // Tear down the AudioContext on unmount so we don't leak resources.
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      try {
        if (sourceRef.current) sourceRef.current.stop(0);
      } catch (e) { void e; }
      try { ctxRef.current?.close(); } catch (e) { void e; }
      ctxRef.current = null;
    };
  }, []);

  return { activeSlot, time, duration, play, stop, toggle, swap };
}
